import React, { ChangeEvent, FC, KeyboardEvent, memo, useState, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';

import emailChipsStyles from './emailChipsStyles';
import { checkIsValidEmail } from './checkIsValidEmail';

export type EmailsError = '' | 'Duplicate Email' | 'Not Valid Email' | 'Not Email';
const NOT_VALID_EMAIL: EmailsError = 'Not Valid Email';
const NOT_EMAIL: EmailsError = 'Not Email';
const DUPLICATE_EMAIL: EmailsError = 'Duplicate Email';

type Props = {
    initEmailChips?: string[];
    onEmailChipsUpdated: (chipEmails: string[]) => void;
    onEmailError?: (emailError: EmailsError) => void;
    emailsChipClassNameProps?: string;
};
const EmailChips: FC<Props> = (props: Props) => {
    const { initEmailChips, onEmailChipsUpdated, onEmailError, emailsChipClassNameProps } = props;
    const classes = emailChipsStyles();

    // Seeded once, at mount. The prop was accepted and then ignored, so every caller
    // passing defaults got an empty field. Seeding on mount rather than syncing on every
    // render is deliberate: the parent re-renders as the user types, and re-applying the
    // prop then would delete addresses out from under them.
    const [chipEmails, setChipEmails] = React.useState<string[]>(() => initEmailChips || []);

    const [emailError, setEmailError] = useState<EmailsError>('');

    const [inputValue, setInputValue] = useState<string>('');
    const [selectedChipIndex, setSelectedChipIndex] = useState<number | null>(null);

    const handleDelete = useCallback(
        (chipIdx: number) => {
            const newChipEmails = [...chipEmails];
            newChipEmails.splice(chipIdx, 1);
            setChipEmails(newChipEmails);
            onEmailChipsUpdated(newChipEmails);

            if (!newChipEmails.length) {
                setSelectedChipIndex(null);
            }
            if (selectedChipIndex === newChipEmails.length) {
                setSelectedChipIndex(newChipEmails.length - 1);
            }
        },
        [chipEmails, onEmailChipsUpdated, selectedChipIndex]
    );

    const updateEmailError = useCallback(
        (value: EmailsError) => {
            if (value !== emailError) {
                setEmailError(value);
                if (onEmailError) {
                    onEmailError(value);
                }
            }
        },
        [onEmailError, emailError]
    );

    const addEmail = useCallback(
        (email: string) => {
            if (!email) {
                updateEmailError(NOT_EMAIL);
                return;
            }
            const emailLowerCase = email.toLowerCase();
            const isValidEmail = emailLowerCase && checkIsValidEmail(emailLowerCase);
            if (!isValidEmail) {
                updateEmailError(NOT_VALID_EMAIL);
                return;
            }
            const newChipEmails = [...chipEmails];
            const duplicatedValues = newChipEmails.indexOf(emailLowerCase);
            if (duplicatedValues !== -1) {
                updateEmailError(DUPLICATE_EMAIL);
                return;
            }
            newChipEmails.push(emailLowerCase);
            setChipEmails(newChipEmails);
            onEmailChipsUpdated(newChipEmails);
            setInputValue('');
        },
        [updateEmailError, chipEmails, onEmailChipsUpdated]
    );

    const handleKeyUp = useCallback(
        (ev: KeyboardEvent<HTMLDivElement>) => {
            const inputValue = (ev.target as HTMLInputElement).value || '';
            if (selectedChipIndex !== null) {
                if (!inputValue && ev.key === 'Backspace') {
                    handleDelete(selectedChipIndex);
                    return;
                }
            } else {
                if (ev.key === 'Enter') {
                    addEmail(inputValue);
                    return;
                }
            }
            if (!inputValue && (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight')) {
                if (selectedChipIndex !== null) {
                    if (ev.key === 'ArrowLeft') {
                        if (selectedChipIndex) {
                            setSelectedChipIndex(selectedChipIndex - 1);
                        }
                    }
                    if (ev.key === 'ArrowRight') {
                        if (selectedChipIndex !== chipEmails.length - 1) {
                            setSelectedChipIndex(selectedChipIndex + 1);
                        } else {
                            setSelectedChipIndex(null);
                        }
                    }
                } else {
                    if (ev.key === 'ArrowLeft') {
                        setSelectedChipIndex(chipEmails.length - 1);
                    }
                }
            }
            if (inputValue || ev.key === 'Escape') {
                setSelectedChipIndex(null);
            }
        },
        [addEmail, chipEmails.length, handleDelete, selectedChipIndex]
    );

    const handleChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            if (emailError === NOT_VALID_EMAIL && checkIsValidEmail(ev.target.value)) {
                updateEmailError('');
            }
            if (emailError === DUPLICATE_EMAIL || emailError === NOT_EMAIL) {
                updateEmailError('');
            }
            setInputValue(ev.target.value);
        },
        [emailError, updateEmailError]
    );

    return (
        <TextField
            className={`${classes.emailsChipStyle} ${emailsChipClassNameProps}`}
            label="Emails"
            name="emails"
            value={inputValue}
            InputProps={{
                className: classes.wrapInputStyle,
                startAdornment: chipEmails.map((email, idx) => {
                    return (
                        <Chip
                            key={idx}
                            variant="outlined"
                            color="secondary"
                            size="small"
                            tabIndex={-1}
                            label={email}
                            className={`${classes.chipStyle} ${
                                selectedChipIndex === idx ? classes.backgroundColorPrimary : ''
                            }`}
                            onDelete={() => handleDelete(idx)}
                        />
                    );
                })
            }}
            inputProps={{
                className: classes.inputStyle
            }}
            onBlur={(ev) => {
                if (ev.target.value) {
                    addEmail(ev.target.value);
                }
            }}
            onKeyUp={handleKeyUp}
            onChange={handleChange}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            error={!!emailError}
            helperText={emailError}
        />
    );
};

export default memo(EmailChips);
